import { Send } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';
import type { DebugLinkedInMessageAuthor } from '@/modules/debug/linkedin/types';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import { Tabs, TabsList, TabsTrigger } from '@/modules/ui/components/tabs/Tabs';
import { Textarea } from '@/modules/ui/components/textarea/Textarea';

interface ComposerProps {
  role: DebugLinkedInMessageAuthor;
  onRoleChange: (role: DebugLinkedInMessageAuthor) => void;
  onSend: (text: string) => void;
  sending: boolean;
  error: string | null;
  /** Candidates can only reply once a recruiter message has been delivered. */
  canReplyAsCandidate: boolean;
}

export function Composer({
  role,
  onRoleChange,
  onSend,
  sending,
  error,
  canReplyAsCandidate,
}: ComposerProps) {
  const [text, setText] = useState('');

  const isRecruiter = role === 'Recruiter';
  const blocked = !isRecruiter && !canReplyAsCandidate;

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || blocked) return;
    onSend(trimmed);
    setText('');
  };

  return (
    <div className="border-t bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <Tabs
          value={role}
          onValueChange={(v) => onRoleChange(v as DebugLinkedInMessageAuthor)}
        >
          <TabsList>
            <TabsTrigger value="Recruiter">Send as recruiter</TabsTrigger>
            <TabsTrigger value="Candidate">Reply as candidate</TabsTrigger>
          </TabsList>
        </Tabs>
        <Badge
          variant="outline"
          className={cn(
            'text-xs',
            isRecruiter ? 'border-[#0a66c2] text-[#0a66c2]' : 'border-muted',
          )}
        >
          {isRecruiter ? 'Outbound' : 'Inbound'}
        </Badge>
      </div>
      <div className="flex items-end gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            blocked
              ? "The candidate can't reply until outreach is sent. Use Send now on the scheduled message."
              : isRecruiter
                ? 'Message the candidate as the Finlay user…'
                : 'Reply as the candidate…'
          }
          rows={3}
          disabled={sending || blocked}
          className="resize-none"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button
          onClick={handleSend}
          loading={sending}
          disabled={blocked || !text.trim()}
        >
          <Send className="mr-2 size-4" />
          Send
        </Button>
      </div>
      {error ? (
        <InlineError className="mt-2">{error}</InlineError>
      ) : (
        <p className="text-muted-foreground mt-2 text-xs">
          Cmd/Ctrl + Enter to send.
        </p>
      )}
    </div>
  );
}
